import { buildApiUrl, fetchJSON, validateRequiredParams } from '../crm/crmApi';

export type QuoteStatus = 'draft' | 'pending' | 'accepted' | 'rejected' | 'expired';

export interface QuoteInput {
  organization_id: string;
  supplier_id: string;
  quote_number?: string;
  status?: QuoteStatus;
  amount?: number;
  currency?: string;
  issued_at?: string;
  valid_until?: string;
  notes?: string;
}

export interface Quote extends QuoteInput {
  id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ListQuotesParams {
  organization_id: string;
  supplier_id?: string;
  status?: Quote['status'];
  limit?: number;
  offset?: number;
}

const quotesService = {
  async list(params: ListQuotesParams): Promise<{ data: Quote[]; count: number | null }>{
    validateRequiredParams(params, ['organization_id'], {
      service: 'supplierCrm.quotesService.list'
    });
    const url = buildApiUrl('/api/supplier-crm/quotes', params as any);
    return fetchJSON(url, {
      description: 'List supplier CRM quotes',
      context: { organization_id: params.organization_id, supplier_id: params.supplier_id, status: params.status }
    });
  },
  async get(id: string, organization_id: string): Promise<{ data: Quote | null }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierCrm.quotesService.get'
    });
    const url = buildApiUrl(`/api/supplier-crm/quotes/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      description: 'Get supplier CRM quote',
      context: { id, organization_id }
    });
  },
  async create(payload: QuoteInput): Promise<{ data: Quote }>{
    validateRequiredParams(payload, ['organization_id', 'supplier_id'], {
      service: 'supplierCrm.quotesService.create'
    });
    const url = buildApiUrl('/api/supplier-crm/quotes');
    return fetchJSON(url, {
      method: 'POST',
      body: payload,
      description: 'Create supplier CRM quote',
      context: { organization_id: payload.organization_id, supplier_id: payload.supplier_id }
    });
  },
  async update(id: string, payload: Partial<QuoteInput> & { organization_id: string }): Promise<{ data: Quote }>{
    validateRequiredParams({ id, ...payload }, ['id', 'organization_id'], {
      service: 'supplierCrm.quotesService.update'
    });
    const url = buildApiUrl(`/api/supplier-crm/quotes/${encodeURIComponent(id)}`);
    return fetchJSON(url, {
      method: 'PUT',
      body: { ...payload, id },
      description: 'Update supplier CRM quote',
      context: { id, organization_id: payload.organization_id }
    });
  },
  async remove(id: string, organization_id: string): Promise<{ success: boolean }>{
    validateRequiredParams({ id, organization_id }, ['id', 'organization_id'], {
      service: 'supplierCrm.quotesService.remove'
    });
    const url = buildApiUrl(`/api/supplier-crm/quotes/${encodeURIComponent(id)}`, { organization_id });
    return fetchJSON(url, {
      method: 'DELETE',
      description: 'Delete supplier CRM quote',
      context: { id, organization_id }
    });
  }
};

export default quotesService;
